import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}) => {
  // Don't render pagination if there's only one page
  if (totalPages <= 1) return null;

  const handlePrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number, e: React.MouseEvent) => {
    e.preventDefault();
    onPageChange(page);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers: (string | number)[] = [];
    const maxPagesToShow = 5; // Show at most 5 page numbers

    if (totalPages <= maxPagesToShow) {
      // If we have 5 or fewer pages, show all of them
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always include first page
      pageNumbers.push(1);

      // Calculate start and end of page numbers to show
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're at the beginning
      if (currentPage <= 2) {
        end = Math.min(totalPages - 1, 4);
      }

      // Adjust if we're at the end
      if (currentPage >= totalPages - 1) {
        start = Math.max(2, totalPages - 3);
      }

      // Add ellipsis if needed
      if (start > 2) {
        pageNumbers.push("ellipsis-start");
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis if needed
      if (end < totalPages - 1) {
        pageNumbers.push("ellipsis-end");
      }

      // Always include last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <nav
      className={`flex justify-center my-6 ${className}`}
      aria-label="Pagination"
    >
      <ul className="flex list-none p-0 m-0 gap-1">
        <li
          className={`m-0 ${
            currentPage === 1 ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <a
            href="#"
            className="inline-flex items-center justify-center min-w-8 h-8 px-3 rounded bg-secondaryBg text-secondary border border-zinc-800 no-underline text-sm transition-colors hover:bg-zinc-900"
            onClick={handlePrevious}
            aria-label="Previous page"
          >
            Previous
          </a>
        </li>

        {getPageNumbers().map((page, index) => {
          if (page === "ellipsis-start" || page === "ellipsis-end") {
            return (
              <li
                key={`ellipsis-${index}`}
                className="flex items-center justify-center min-w-8 h-8 text-secondary"
              >
                <span>...</span>
              </li>
            );
          }

          return (
            <li key={`page-${page}`} className="m-0">
              <a
                href="#"
                className={`inline-flex items-center justify-center min-w-8 h-8 px-2 rounded text-sm no-underline transition-colors border 
                  ${
                    currentPage === page
                      ? "bg-secondaryBg text-secondary border-highlight"
                      : "bg-secondaryBg text-secondary border-zinc-800 hover:bg-zinc-900"
                  }`}
                onClick={(e) => handlePageClick(page as number, e)}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </a>
            </li>
          );
        })}

        <li
          className={`m-0 ${
            currentPage === totalPages ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <a
            href="#"
            className="inline-flex items-center justify-center min-w-8 h-8 px-3 rounded bg-secondaryBg text-secondary border border-zinc-800 no-underline text-sm transition-colors hover:bg-zinc-900"
            onClick={handleNext}
            aria-label="Next page"
          >
            Next
          </a>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
